import classNames from "classnames";
import {
  IWebsiteBuilderElementBase,
  IWebsiteBuilderElementDataBase,
  TWebsiteBuilderElement,
} from "./websiteBuilderElement";
import { websiteBuilderNormalizeHexColor } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { isWebsiteBuilderParentNode, IWebsiteBuilderNodeDesignStyles, TWebsiteBuilderNode } from "../websiteBuilderNode";
import { CSSProperties } from "react";
import { IWebsiteBuilderIconAsset, IWebsiteBuilderIconAssetIdentityRaw, TWebsiteBuilderWebsiteVersionPage } from "redux/reducers/models/websiteBuilder";
import { CancelToken } from "axios";
import { IWebsiteBuilderHeader } from "../section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "../section/websiteBuilderFooter";
import { RetrieveWebsiteBuilderIconAssetsByIdentities } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { StatusCode } from "api/protocols";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { TWebsiteBuilderOptionalLink, websiteBuilderNormalizeOptionalLink } from "./websiteBuilderLink";

export const WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M8.5 6H15.5M12 6V18M8.5 18H15.5" stroke="black" stroke-width="1.75" stroke-linecap="round"/><path d="M5 4L19 20" stroke="black" stroke-width="1.75" stroke-linecap="round"/></svg>`;

export const WEBSITE_BUILDER_TEMPLATE_ICON_ASSET_IDENTITIES = {
  facebookLogo: { providerCode: "phosphor", versionCode: "v1", variantCode: "fill", assetCode: "facebook-logo" },
  instagramLogo: { providerCode: "phosphor", versionCode: "v1", variantCode: "fill", assetCode: "instagram-logo" },
  twitterLogo: { providerCode: "phosphor", versionCode: "v1", variantCode: "fill", assetCode: "twitter-logo" },
  phone: { providerCode: "phosphor", versionCode: "v1", variantCode: "fill", assetCode: "phone" },
  envelopeSimple: { providerCode: "phosphor", versionCode: "v1", variantCode: "fill", assetCode: "envelope-simple" },
  golf: { providerCode: "phosphor", versionCode: "v1", variantCode: "regular", assetCode: "golf" },
  star: { providerCode: "phosphor", versionCode: "v1", variantCode: "regular", assetCode: "star" },
  listBullets: { providerCode: "phosphor", versionCode: "v1", variantCode: "regular", assetCode: "list-bullets" },
} satisfies Record<string, IWebsiteBuilderIconAssetIdentity>;

export const WEBSITE_BUILDER_SUGGESTED_ICON_IDENTITIES: IWebsiteBuilderIconAssetIdentity[] = [
  "golf", "calendar-blank", "trophy", "map-pin",
  "warning", "info", "phone-call", "envelope-simple",
  "star", "arrow-up", "check-circle", "flag",
].map(assetCode => ({ providerCode: "phosphor", versionCode: "v1", variantCode: "regular", assetCode }));

export interface IWebsiteBuilderIconAssetIdentity {
  providerCode: string;
  versionCode: string;
  variantCode: string;
  assetCode: string;
}

export interface IWebsiteBuilderIconSettings {
  assetIdentity: IWebsiteBuilderIconAssetIdentity;
  color: string;
}

export type TWebsiteBuilderIconElementSettings = IWebsiteBuilderIconSettings & {
  link: TWebsiteBuilderOptionalLink;
};

interface IWebsiteBuilderIconData extends IWebsiteBuilderElementDataBase {
  type: "icon";
  settings: TWebsiteBuilderIconElementSettings;
}

export interface IWebsiteBuilderIcon extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderIconData;
}

export function isWebsiteBuilderIcon(node: TWebsiteBuilderElement): node is IWebsiteBuilderIcon {
  return node?.element?.type === "icon";
}

export function isWebsiteBuilderIconAssetIdentity(value: unknown): value is IWebsiteBuilderIconAssetIdentity {
  return (
    value != null &&
    typeof value === "object" &&
    "providerCode" in value &&
    typeof value.providerCode === "string" &&
    "versionCode" in value &&
    typeof value.versionCode === "string" &&
    "variantCode" in value &&
    typeof value.variantCode === "string" &&
    "assetCode" in value &&
    typeof value.assetCode === "string"
  );
}

export function websiteBuilderGenerateDefaultIconSettings(assetIdentity: IWebsiteBuilderIconAssetIdentity = { ...WEBSITE_BUILDER_TEMPLATE_ICON_ASSET_IDENTITIES.golf }): IWebsiteBuilderIconSettings {
  return {
    assetIdentity,
    color: null,
  };
}

export function websiteBuilderGenerateDefaultIcon(assetIdentity: IWebsiteBuilderIconAssetIdentity = { ...WEBSITE_BUILDER_TEMPLATE_ICON_ASSET_IDENTITIES.golf }): IWebsiteBuilderIcon {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "icon",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 30, height: 30 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 30, height: 30 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 30, height: 30 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: {
        ...websiteBuilderGenerateDefaultIconSettings(assetIdentity),
        link: { type: "none" },
      },
    },
  };
}

export function websiteBuilderNormalizeIconSettings(
  partialSettings: Partial<IWebsiteBuilderIconSettings>,
): IWebsiteBuilderIconSettings {
  const defaultSettings = websiteBuilderGenerateDefaultIconSettings(null);

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { assetIdentity, color } = partialSettings;

  const normalizedIconSettings: IWebsiteBuilderIconSettings = {
    assetIdentity: isWebsiteBuilderIconAssetIdentity(assetIdentity)
      ? {
          providerCode: assetIdentity.providerCode,
          versionCode: assetIdentity.versionCode,
          variantCode: assetIdentity.variantCode,
          assetCode: assetIdentity.assetCode,
        }
      : defaultSettings.assetIdentity,
    color: websiteBuilderNormalizeHexColor(color, defaultSettings.color, false),
  };

  return normalizedIconSettings;
}

export function websiteBuilderNormalizeIconElementSettings(
  partialSettings: Partial<TWebsiteBuilderIconElementSettings>,
): TWebsiteBuilderIconElementSettings {
  return {
    ...websiteBuilderNormalizeIconSettings(partialSettings),
    link: websiteBuilderNormalizeOptionalLink(partialSettings?.link),
  };
}

interface IWebsiteBuilderIconDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
}

export function websiteBuilderGenerateIconDesignStyles(
  settings: IWebsiteBuilderIconSettings,
): IWebsiteBuilderIconDesignStyles {
  const styleProperties: CSSProperties = {
    color: settings.color,
  };

  let styleDeclaration = "";

  if (styleProperties.color) {
    styleDeclaration += `color: ${styleProperties.color};`;
  }

  return {
    containerClassName: classNames("website-builder-icon-container", {
      "no-asset-identity-border": settings.assetIdentity == null,
    }),
    className: "website-builder-icon",
    styleProperties,
    styleDeclaration,
  };
}

export function websiteBuilderFindIconAsset(
  iconAssets: IWebsiteBuilderIconAsset[],
  assetIdentity: IWebsiteBuilderIconAssetIdentity,
): IWebsiteBuilderIconAsset {
  if (
    assetIdentity == null ||
    assetIdentity.providerCode == null ||
    assetIdentity.versionCode == null ||
    assetIdentity.variantCode == null ||
    assetIdentity.assetCode == null
  ) {
    return null;
  }

  return iconAssets?.find(iconAsset => (
    iconAsset?.provider_code === assetIdentity.providerCode &&
    iconAsset?.version_code === assetIdentity.versionCode &&
    iconAsset?.variant_code === assetIdentity.variantCode &&
    iconAsset?.asset_code === assetIdentity.assetCode
  )) ?? null;
}

export function websiteBuilderResolveIconAssetIdentity(
  iconAssets: IWebsiteBuilderIconAsset[],
  assetIdentity: IWebsiteBuilderIconAssetIdentity,
) {
  if (websiteBuilderFindIconAsset(iconAssets, assetIdentity) == null) {
    return null;
  } else {
    return {
      providerCode: assetIdentity.providerCode,
      versionCode: assetIdentity.versionCode,
      variantCode: assetIdentity.variantCode,
      assetCode: assetIdentity.assetCode,
    };
  }
}

export async function websiteBuilderLoadIconAssets(
  token: CancelToken,
  header?: IWebsiteBuilderHeader,
  footer?: IWebsiteBuilderFooter,
  pages?: TWebsiteBuilderWebsiteVersionPage[],
): Promise<IWebsiteBuilderIconAsset[]> {
  const identitiesByKey = new Map<string, IWebsiteBuilderIconAssetIdentityRaw>();

  function addIdentity(identity: IWebsiteBuilderIconAssetIdentity) {
    if (identity == null) {
      return;
    }

    const key = JSON.stringify([identity.providerCode, identity.versionCode, identity.variantCode, identity.assetCode]);

    identitiesByKey.set(key, {
      provider_code: identity.providerCode,
      version_code: identity.versionCode,
      variant_code: identity.variantCode,
      asset_code: identity.assetCode,
    });
  }

  function traverse(node: TWebsiteBuilderNode) {
    if (node == null) {
      return;
    }

    if (node.type === "element") {
      if (node.element.type === "icon") {
        addIdentity(node.element.settings.assetIdentity);
      } else if (node.element.type === "button") {
        addIdentity(node.element.settings.icon?.assetIdentity);
      }
    } else if (node.type === "container" && node.container.type === "marquee") {
      node.container.settings.items.forEach(item => {
        if (item.type === "icon") {
          addIdentity(item.settings.assetIdentity);
        }
      });
    } else if (node.type === "container" && node.container.type === "table") {
      const { head, body } = node.container.settings;

      const rows = [head.row, ...body.rows];

      for (const row of rows) {
        for (const cell of row.cells) {
          if (cell.value.type === "icon") {
            addIdentity(cell.value.assetIdentity);
          }
        }
      }
    }

    if (isWebsiteBuilderParentNode(node)) {
      node.children.forEach(traverse);
    }
  }

  Object.values(WEBSITE_BUILDER_TEMPLATE_ICON_ASSET_IDENTITIES).forEach(addIdentity);
  traverse(header);
  traverse(footer);
  pages?.forEach(page => traverse(page.root));

  const identities = Array.from(identitiesByKey.values());
  const iconAssets: IWebsiteBuilderIconAsset[] = [];

  for (let index = 0; index < identities.length; index += 500) {
    const res = await RetrieveWebsiteBuilderIconAssetsByIdentities({ identities: identities.slice(index, index + 500) }, false, token);

    if (res.status !== StatusCode.OK) {
      return null;
    }

    iconAssets.push(...res.data);
  }

  return iconAssets;
}